import React, { forwardRef, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Body2, OverFlowBody2 } from '../../atoms/Text';

const DropDownContainer = styled.div`
    position: absolute;
    z-index: 100;
    width: 100%;
    max-height: 207px;
    height: fit-content;
    display: ${(props) => (props.open ? 'block' : 'none')};
    padding: 12px;
    align-items: center;
    box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1), 0px 2px 2px rgba(0, 0, 0, 0.03);
    border-radius: 12px;
    background-color: white;
    overflow: auto;

    ${(props) =>
        props.direction === 'down'
            ? css`
                  top: 4px;
              `
            : css`
                  bottom: 44px;
              `}
`;

const DropDownListRenderItem = styled.div`
    padding-top: 3px;
    padding-bottom: 3px;
    display: flex;
    width: 100%;
    border-radius: 6px;
    cursor: pointer;

    background-color: ${(props) => (props.selected ? '#efedff' : 'transparent')};

    & > div {
        width: 100%;
        box-sizing: border-box;
        -moz-box-sizing: border-box;
        -webkit-box-sizing: border-box;
        border-radius: 6px;
        filter: ${(props) =>
            props.selected && props.type === 'workload'
                ? 'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.12)) drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.24))'
                : 'none'};
    }
`;

const DropDownListItem = styled.div`
    padding-top: 3px;
    padding-bottom: 3px;
    display: flex;
    width: 100%;
    border-radius: 6px;
    cursor: pointer;

    background-color: ${(props) => (props.selected ? '#efedff' : 'transparent')};

    & > div {
        box-sizing: border-box;
        -moz-box-sizing: border-box;
        -webkit-box-sizing: border-box;
        border-radius: 6px;
        filter: ${(props) =>
            props.selected && props.type === 'workload'
                ? 'drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.12)) drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.24))'
                : 'none'};
    }
`;

const DropDownText = styled(Body2)`
    text-align: left;
    margin: 0 8px;
`;

const OverflowDropDownText = styled(OverFlowBody2)`
    text-align: left;
    margin: 0 8px;
`;

const DropDown = forwardRef(
    (
        {
            currentSelection,
            className,
            open,
            direction,
            renderOption,
            shouldIgnoreOverflow = false,
            shouldPreventDefault = false,
            shouldStopPropagation = false,
            itemList,
            handleSelected,
            handleCancel,
            type,
        },
        ref
    ) => {
        const [selectedIndex, setSelectedIndex] = useState(0);
        let selectedItem =
            itemList.length > 0 && itemList[selectedIndex] !== undefined ? itemList[selectedIndex].value : undefined;

        const handleClick = (e, option) => {
            if (shouldPreventDefault && e.preventDefault) e.preventDefault();
            if (shouldStopPropagation && e.stopPropagation) e.stopPropagation();
            handleSelected(e, option);
        };

        const handleClose = () => {
            handleCancel();
        };

        const handleKeyDown = (e) => {
            e.preventDefault();

            if (e.key === 'ArrowDown') {
                selectedIndex >= itemList.length - 1 ? setSelectedIndex(0) : setSelectedIndex(selectedIndex + 1);
            } else if (e.key === 'ArrowUp') {
                selectedIndex <= 0 ? setSelectedIndex(itemList.length - 1) : setSelectedIndex(selectedIndex - 1);
            } else if (e.key === 'Enter') {
                handleClick(e, itemList[selectedIndex]);
            } else if (e.key === 'Escape') {
                handleClose();
            }

            selectedItem = itemList[selectedIndex].value;
        };

        const getSelection = (value) => {
            let selection = itemList.findIndex((item) => {
                return item.value === value;
            });

            selection > 0 ? setSelectedIndex(selection) : setSelectedIndex(0);
        };

        useEffect(() => {
            if (currentSelection !== undefined) {
                getSelection(currentSelection.value);
            }
        }, [open]);

        return (
            <DropDownContainer
                tabIndex={'1'}
                onKeyDown={handleKeyDown}
                ref={ref}
                className={className}
                open={open}
                direction={direction}
            >
                {itemList &&
                    itemList.map(({ title, value }) => {
                        return renderOption ? (
                            <DropDownListRenderItem
                                tabIndex={'0'}
                                key={value?.key || value}
                                type={type}
                                selected={selectedItem === value}
                                id={value}
                                onMouseEnter={() => getSelection(value)}
                                onMouseDown={(e) => handleClick(e, { title, value })}
                            >
                                {shouldIgnoreOverflow ? (
                                    <Body2 as={'div'}>{renderOption({ title, value })}</Body2>
                                ) : (
                                    <OverFlowBody2 as={'div'} maxWidth={'200px'}>
                                        {renderOption({ title, value })}
                                    </OverFlowBody2>
                                )}
                            </DropDownListRenderItem>
                        ) : (
                            <DropDownListItem
                                tabIndex={'0'}
                                key={value?.key || value}
                                type={type}
                                id={value}
                                selected={selectedItem === value}
                                onMouseEnter={() => getSelection(value)}
                                onMouseDown={(e) => handleClick(e, { title, value })}
                            >
                                {shouldIgnoreOverflow ? (
                                    <DropDownText>{title}</DropDownText>
                                ) : (
                                    <OverflowDropDownText maxWidth={'200px'}>{title}</OverflowDropDownText>
                                )}
                            </DropDownListItem>
                        );
                    })}
            </DropDownContainer>
        );
    }
);

export default DropDown;
